import { useCallback, useContext, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Incidents from '@/modules/common/pages/Incidents.tsx';
import { useClientsListQuery } from '@/modules/common/queries/clients.ts';
import ClientCommunicationsTab from '@/modules/admin-dashboard/components/communications/ClientCommunicationsTab.tsx';
import RecentActivity from '@/modules/admin-dashboard/components/dashboard/RecentActivity.tsx';
import { EditClientModal } from '@/modules/admin-dashboard/components/modals/EditClientModal.tsx';
import Documents from '@/modules/admin-dashboard/pages/Documents.tsx';
import OnDemandReports from '@/modules/admin-dashboard/pages/OnDemandReports.tsx';
import Tasks from '@/modules/admin-dashboard/pages/Tasks.tsx';
import { adminDashboardPaths } from '@/modules/admin-dashboard/routes.tsx';
import {
  DropdownMenu,
  DropdownMenuItem,
  ModalProviderContext,
  PageLayout,
  TabItem,
  Tabs,
} from 'ui';
import { ArrowLeftIcon, DotsHorizontalIcon } from 'ui/icons';

const TAB_TASKS = 'tasks';
const TAB_EVENTS = 'events';
const TAB_REPORTS = 'reports';
const TAB_DOCUMENTS = 'documents';
const TAB_ACTIVITY = 'activity';
const TAB_COMMUNICATIONS = 'communications';

const Client = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { openModal } = useContext(ModalProviderContext);
  const [searchParams] = useSearchParams();

  // Initialize active tab from URL or default to tasks
  const [activeTab, setActiveTab] = useState<string>(() => {
    const tabFromUrl = searchParams.get('tab');
    return tabFromUrl || TAB_TASKS;
  });

  // Fetch all clients and find the current one
  const { data } = useClientsListQuery({
    page: 1,
    page_size: 100,
  });

  const client = useMemo(() => {
    const allClients = (data?.pages || []).flatMap(({ data }) => data);
    return allClients.find(c => c.id === Number(id));
  }, [data?.pages, id]);

  const clientName = client?.name || `Client #${id}`;

  const handleEdit = useCallback(() => {
    if (client) {
      openModal(EditClientModal, { client });
    }
  }, [client, openModal]);

  const menuOptions = useMemo<DropdownMenuItem[]>(
    () => [
      {
        label: 'Edit',
        onClick: handleEdit,
      },
    ],
    [handleEdit],
  );

  const tabItems: TabItem[] = useMemo(
    () => [
      {
        name: TAB_TASKS,
        label: 'Tasks',
        container: <Tasks clientId={Number(id)} />,
      },
      {
        name: TAB_EVENTS,
        label: 'Events',
        container: <Incidents hideAddIncidentTypeButton clientId={Number(id)} />,
      },
      {
        name: TAB_REPORTS,
        label: 'Reports',
        container: <OnDemandReports clientId={Number(id)} />,
      },
      {
        name: TAB_DOCUMENTS,
        label: 'Documents',
        container: <Documents clientId={Number(id)} />,
      },
      {
        name: TAB_COMMUNICATIONS,
        label: 'Communications',
        container: client ? <ClientCommunicationsTab client={client} /> : null,
      },
      {
        name: TAB_ACTIVITY,
        label: 'Audit logs',
        container: <RecentActivity clientId={Number(id)} fullHeight />,
      },
    ],
    [client, id],
  );

  return (
    <PageLayout
      title={
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() =>
                navigate({
                  pathname: adminDashboardPaths.static.clients,
                  search: location.search,
                })
              }
              className="flex items-center justify-center w-8 h-8 rounded hover:bg-gray-100 transition-colors"
              aria-label="Go back"
            >
              <img src={ArrowLeftIcon} alt="Back" className="w-6 h-6" />
            </button>
            <span className="text-2xl font-semibold text-gray-900 truncate font-sans">
              {clientName}
            </span>
          </div>
          <DropdownMenu
            autoPlace
            items={menuOptions}
            button={
              <button className="flex items-center justify-center w-8 h-8 rounded hover:bg-gray-100 transition-colors">
                <img src={DotsHorizontalIcon} alt="Menu" className="w-6 h-6" />
              </button>
            }
            className="w-[160px] divide-white"
            containerClassName="w-auto"
          />
        </div>
      }
      tabs={tabItems}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      tabNameParam="tab"
      contentClassName="!p-0"
      classNameTitle="[&>div:first-child]:flex-1"
    >
      <Tabs items={tabItems} activeTab={activeTab} />
    </PageLayout>
  );
};

export default Client;
